/**
 * Arrived (Spanish: Llegué) — the crew's step before Finish on today's visits (1.4.0). Three answers:
 *   Tenant is home            → the visit starts (optional Before photos)
 *   No answer                 → Record No Show, "no answer" (optional note and photo)
 *   Tenant cancelled at door  → a reason, then "Come back later today?": a free time today (only while
 *                               pick-a-spot is on), or Needs a new day. Nobody is dropped.
 * The crew sees today only and never books anyone new. "Entry permission on file for today" shows when the
 * tenant agreed on their own link; "Needs the tenant home" when they did not (or have not answered).
 *
 * 1.5.16 — one sheet, the choices in this order:
 *   Tenant is home                                      → the visit starts (optional Before photos)
 *   Tenant is not home — try again later today          → the office is told and decides (optional note and photo);
 *                                                         the visit stays on the list, the crew picks no new time
 *   Tenant cancelled at the door                        → as above
 *   Tenant is not home — permission to enter is on file → the visit starts, recorded as entered with permission.
 *                                                         Rendered only when a permission is on file for this visit
 *                                                         (the tenant's Yes by email, or recorded by the office by
 *                                                         phone); the server refuses it otherwise.
 *
 * 1.5.17 — the crew sees whether a permission is on file either way. Both "not home" lines are always there, one
 * live and the other greyed (a disabled radio, "Not available" and the reason in words, tied to the radio):
 *   Tenant is home · Tenant cancelled at the door ·
 *   Tenant is not home — no permission to enter on file   → the office is told and decides (the 1.5.16 `noAnswer`);
 *                                                           greyed "permission on file, use the line below" when
 *                                                           one is on file
 *   Tenant is not home — permission to enter on file: by email 5 Oct → the visit starts (`permission`); greyed
 *                                                           "no permission on file — call the office" when none is
 * and the note at the top says "Permission to enter: received … by …" or "… not on file".
 */
import { useMemo, useState, type RefObject } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { PhotoUploadResponse, TodayVisit, TxView } from '@shared/api-types';
import { DOOR_REASONS, startsVisit, type ArrivalAnswer, type DoorNext, type DoorReason, type EntryPermissionMarker } from '@shared/arrival';
import { serviceDateKey, serviceMinutesNow } from '@shared/dates';
import { formatClockOf, formatMonthDay } from '@/lib/dates';
import { clockWithPeriod, formatClock } from '@shared/dayplan';
import { newFormSession } from '@shared/envelopes/queue1';
import { apiFetch, ApiRequestError } from '@/lib/api';
import { useOfflineQueue } from '@/features/offline/OfflineQueueProvider';
import { isNetworkFailure } from '@/features/offline/queue';
import { pressName } from '@shared/press';
import { useNow } from '@/features/press/Press';
import { useTx } from '@/lib/tx';
import { useDirtyFlag } from '@/lib/dirty';
import { useAnnounce } from '@/components/Announcer';
import { Icon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { SheetContent } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { PhotoPicker, type PhotoItem } from '@/features/finish/Photos';
import { WhenControl, type WhenValue } from '@/features/daybend/WhenControl';

interface LaterResponse {
  pickSpot: boolean;
  spots: { position: number; startMinutes: number }[];
}
interface ArrivedResponse {
  transactions: TxView[];
  followUpId: string | null;
  mock?: boolean;
}

async function uploadAll(items: PhotoItem[], appointmentId: string, purpose: 'before' | 'door', into?: string[]): Promise<string[]> {
  const out: string[] = into ?? [];
  let n = 0;
  for (const p of items) {
    if (!p.blob) continue;
    n++;
    const fd = new FormData();
    fd.set('appointmentId', appointmentId);
    fd.set('purpose', purpose);
    fd.set('file', p.blob, `${purpose}-${n}.jpg`);
    const res = await fetch('/api/documents', { method: 'POST', body: fd, credentials: 'same-origin', headers: { Accept: 'application/json' } });
    const body = (await res.json().catch(() => null)) as (PhotoUploadResponse & { message?: string }) | null;
    if (!res.ok || !body?.path) throw new Error(body?.message ?? `Photo ${n} could not be sent (${res.status}).`);
    out.push(body.path);
  }
  return out;
}

/** How a permission on file was given and the day (New York) it was: "by email", "Oct 5" — one source for every line. */
function usePermissionParts(permission: EntryPermissionMarker | null | undefined): { how: string; date: string | null } {
  const { t, i18n } = useTranslation();
  const at = permission?.at ?? permission?.answeredAt ?? null;
  return { how: t(`door16.how.${permission?.how ?? 'email'}`), date: at ? formatMonthDay(serviceDateKey(new Date(at)), i18n.language) : null };
}

/** "Permission to enter: received Oct 5 by email" (or "by phone") — the words on the card and in the sheet (1.5.17 wording). */
export function PermissionWords({ permission }: { permission: EntryPermissionMarker }) {
  const { t } = useTranslation();
  const { how, date } = usePermissionParts(permission);
  return <>{date ? t('door17.permissionLine', { date, how }) : t('door17.permissionLineNoDate', { how })}</>;
}

export function ArrivedSheet({ visit, time, onDone, returnFocusTo }: { visit: TodayVisit; time: string | null; onDone: () => void; returnFocusTo?: RefObject<HTMLElement | null> }) {
  const { t, i18n } = useTranslation();
  // 1.6.5: the Send says what it will record — "Arrived at 515 · 9:38" (the clock now, kept current)
  const nowIso = useNow(15_000).toISOString();
  const announce = useAnnounce();
  const toast = useToast();
  const tx = useTx();
  const offline = useOfflineQueue();
  const qc = useQueryClient();
  const session = useMemo(() => newFormSession(), []);
  const [answer, setAnswer] = useState<ArrivalAnswer | null>(null);
  const [reason, setReason] = useState<DoorReason | null>(null);
  const [words, setWords] = useState('');
  const [before, setBefore] = useState<PhotoItem[]>([]);
  const [doorPhotos, setDoorPhotos] = useState<PhotoItem[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 1.7.8 (D-1.7.8-1): when the crew began — Now, or an earlier time recorded later (never a time still to come). */
  const [when, setWhen] = useState<WhenValue>({ mode: 'now' });
  const nowMinutes = serviceMinutesNow(new Date(nowIso));
  const booked = visit.chosen && visit.chosen.start < nowMinutes ? visit.chosen.start : null;
  useDirtyFlag('arrived', answer !== null || words !== '' || before.length + doorPhotos.length > 0 || busy); // 1.6.5: lib/app-update.ts

  const permission = visit.entryPermission?.status ?? null;
  const onFile = permission === 'granted';
  const given = usePermissionParts(visit.entryPermission);
  const later = useQuery({
    queryKey: ['arrival-later', visit.appointmentId],
    queryFn: () => apiFetch<LaterResponse>(`/api/housekeeping/later?appointmentId=${encodeURIComponent(visit.appointmentId)}`),
    enabled: answer === 'doorCancel',
    staleTime: 15_000,
  });
  const reasonReady = !!reason && (reason !== 'other' || !!words.trim());

  const send = async (next?: DoorNext, position?: number) => {
    if (!answer) return;
    setBusy(true);
    setError(null);
    const uploaded: string[] = [];
    try {
      // `at` travels only for a time other than Now, and only on the answers that start the visit
      const at = startsVisit(answer) ? (when.mode === 'time' ? when.minutes : null) : null;
      const photoItems = startsVisit(answer) ? before : answer === 'noAnswer' ? doorPhotos : [];
      const purpose = startsVisit(answer) ? 'before' : 'door';
      const label =
        answer === 'home'
          ? t('arrived.labelHome', { unit: visit.unit })
          : answer === 'permission'
            ? t('door16.labelPermission', { unit: visit.unit })
            : next === 'later'
              ? t('arrived.labelLater', { unit: visit.unit })
              : t('arrived.labelNewDay', { unit: visit.unit });
      const request = { session, appointmentId: visit.appointmentId, answer, reason: reason ?? undefined, words: words.trim() || undefined, next, position, ...(at !== null ? { at } : {}) };
      /** 1 Nov 2026 (D-1102-CREW-3): no signal — the press is kept on this phone (same session, so a second send is the same press) and goes when the signal returns */
      const keepOnPhone = async (): Promise<boolean> => {
        const blobs = photoItems.filter((p) => p.blob);
        const r = await offline.keep({
          id: `QA-${session.idempotencyKey}`,
          kind: 'arrived',
          appointmentId: visit.appointmentId,
          unit: visit.unit,
          day: visit.serviceDateKey,
          label,
          endpoint: '/api/housekeeping/arrived',
          body: { ...request, photos: [] },
          photos: blobs.map((p, i) => ({ target: 'photos' as const, purpose, name: `${purpose}-${i + 1}.jpg`, blob: p.blob!, path: uploaded[i], bytes: p.blob!.size })),
        });
        if (r === 'saved') {
          const m = t('crew1102.savedToast', { what: t('crew1102.what.arrived', { unit: visit.unit }) });
          toast(m);
          announce(m);
          onDone();
          return true;
        }
        const m = r === 'full' ? t('crew1102.full', { mb: 40 }) : t('crew1102.storage');
        setError(m);
        announce(m, 'assertive');
        return true;
      };
      // a press already waiting on this phone for this visit keeps its place: this one goes behind it
      if (offline.enabled && (offline.heldFor(visit.appointmentId).length > 0 || (typeof navigator !== 'undefined' && navigator.onLine === false))) {
        await keepOnPhone();
        return;
      }
      let res: ArrivedResponse;
      try {
        const photos = await uploadAll(photoItems, visit.appointmentId, purpose, uploaded);
        res = await apiFetch<ArrivedResponse>('/api/housekeeping/arrived', { method: 'POST', body: JSON.stringify({ ...request, photos }) });
      } catch (e) {
        if (offline.enabled && isNetworkFailure(e)) {
          await keepOnPhone();
          return;
        }
        throw e;
      }
      const main = res.transactions[res.transactions.length - 1];
      if (main) tx.track({ transaction: main, steps: res.transactions, mock: !!res.mock }, { label, unit: visit.unit, appointmentId: visit.appointmentId, dayKey: visit.serviceDateKey });
      // not home: nothing went to the scheduler — the office has it
      const msg = answer === 'noAnswer' ? t('door16.notHomeSent', { unit: visit.unit }) : t('arrived.sent', { label });
      toast(msg);
      announce(msg);
      void qc.invalidateQueries({ queryKey: ['today'] });
      onDone();
    } catch (e) {
      const body = e instanceof ApiRequestError ? (e.body as { error?: string; message?: string; by?: string; closeOutFirst?: { unit: string } } | undefined) : undefined;
      // 1.6.5: another phone was first — say who, close, and let the card redraw with their stamp
      if (body?.error === 'already') {
        const m = body.by ? t('press165.markedArrivedNow', { name: pressName(body.by) }) : t('press165.someoneArrived');
        toast(m);
        announce(m, 'assertive');
        void qc.invalidateQueries({ queryKey: ['today'] });
        onDone();
        return;
      }
      const m = body?.error === 'close-out-first' && body.closeOutFirst ? t('press165.closeOutFirst', { unit: body.closeOutFirst.unit }) : (body?.message ?? (e as Error).message);
      if (body?.error === 'close-out-first') void qc.invalidateQueries({ queryKey: ['today'] });
      setError(m);
      announce(m, 'assertive');
    } finally {
      setBusy(false);
    }
  };

  /** `off`: why this line cannot be chosen now (1.5.17) — the radio is disabled and the reason stands in words. */
  const choice = (a: ArrivalAnswer, icon: 'check' | 'clock' | 'close' | 'lock', title: string, help: string, off?: string) => (
    <label data-testid={`arrived-${a}`} data-live={off ? 'false' : 'true'} className={off ? 'is-off' : undefined}>
      <input type="radio" name="arrived" checked={!off && answer === a} disabled={!!off} aria-describedby={off ? `arrived-${a}-help` : undefined} onChange={() => setAnswer(a)} />
      <Icon name={icon} aria-hidden="true" />
      <span>
        {title}
        <small id={`arrived-${a}-help`}>
          {off ? (
            <>
              <b>{t('door17.notAvailable')}</b> {off}
            </>
          ) : (
            help
          )}
        </small>
      </span>
    </label>
  );

  /** The press that sends the photos: a picked photo is said as ready, sent with it (18 Oct 2026 — "added" is for what the server took). */
  const sendLabel = answer === 'noAnswer' ? t('door16.tellOffice') : t('press165.sendArrived', { unit: visit.unit, time: when.mode === 'time' ? clockWithPeriod(when.minutes, i18n.language.startsWith('es') ? 'es' : 'en') : formatClockOf(nowIso, i18n.language) });
  const photoReady = (count: number) => t('finish.photoReadyWith', { count, button: sendLabel });
  const footer =
    startsVisit(answer) || answer === 'noAnswer' ? (
      <>
        <Button variant="secondary" type="button" onClick={onDone}>
          {t('arrived.close')}
        </Button>
        <Button type="button" disabled={busy} onClick={() => send()} data-testid="arrived-send">
          {busy ? t('arrived.sending') : sendLabel}
        </Button>
      </>
    ) : (
      <Button variant="secondary" type="button" onClick={onDone}>
        {t('arrived.close')}
      </Button>
    );

  return (
    <SheetContent title={t('arrived.title', { unit: visit.unit, time: time ?? '' })} returnFocusTo={returnFocusTo} footer={footer} data-testid="arrived-sheet">
      {permission === 'granted' ? (
        <div className="warn ok" role="note" data-testid="entry-on-file">
          <Icon name="check" />
          <div>
            <b>
              <PermissionWords permission={visit.entryPermission!} />
            </b>
            <br />
            <span>{t('door16.onFileHelp')}</span>
          </div>
        </div>
      ) : (
        // 1.5.17: said either way — none, asked with no answer yet, or answered No are all "not on file" at the door
        <div className="warn" role="note" data-testid="entry-not-on-file">
          <Icon name="alert" />
          <div>
            <b>{t('door17.none')}</b>
            <br />
            <span>{t('door17.noneHelp')}</span>
          </div>
        </div>
      )}

      <fieldset className="field radio-cards" data-testid="arrived-answers">
        <legend>{t('arrived.question')}</legend>
        {choice('home', 'check', t('arrived.home'), t('arrived.homeHelp'))}
        {choice('doorCancel', 'close', t('arrived.doorCancel'), t('arrived.doorCancelHelp'))}
        {/* 1.5.17: both "not home" lines, always — one live, the other greyed with its reason; the server checks again */}
        {choice('noAnswer', 'clock', t('door17.notHomeNone'), t('door17.notHomeNoneHelp'), onFile ? t('door17.notHomeNoneOff') : undefined)}
        {choice(
          'permission',
          'lock',
          !onFile ? t('door17.withPermissionNone') : given.date ? t('door17.withPermission', given) : t('door17.withPermissionNoDate', given),
          t('door17.withPermissionHelp'),
          onFile ? undefined : t('door17.withPermissionOff'),
        )}
      </fieldset>

      {startsVisit(answer) ? (
        <div className="field arrived-when" data-testid="arrived-when-block">
          <WhenControl
            id="arrived-when"
            day={visit.serviceDateKey}
            value={when}
            onChange={(v) => {
              setWhen(v);
              setError(null);
            }}
            allowFuture={false}
            suggest={booked !== null ? { minutes: booked, label: t('book178.bookedFor', { time: formatClock(booked) }) } : null}
          />
        </div>
      ) : null}

      {startsVisit(answer) ? (
        <PhotoPicker id="photo-before" testid="before-photos" items={before} onChange={setBefore} legend={t('arrived.beforePhotos')} pickedWords={photoReady} help={<span>{t('arrived.photosLine')}</span>} />
      ) : null}

      {answer === 'noAnswer' ? (
        <>
          <div className="field">
            <label htmlFor="arrived-note">{t('arrived.noteOpt')}</label>
            <textarea id="arrived-note" className="input" value={words} onChange={(e) => setWords(e.target.value)} />
          </div>
          <PhotoPicker id="photo-door" testid="door-photos" items={doorPhotos} onChange={setDoorPhotos} legend={t('arrived.doorPhoto')} pickedWords={photoReady} />
        </>
      ) : null}

      {answer === 'doorCancel' ? (
        <>
          <fieldset className="field" data-testid="door-reasons">
            <legend>{t('arrived.why')}</legend>
            {DOOR_REASONS.map((r) => (
              <label className="radio" key={r}>
                <input type="radio" name="door-reason" checked={reason === r} onChange={() => setReason(r)} data-testid={`reason-${r}`} />
                <span>{t(`arrived.reason.${r}`)}</span>
              </label>
            ))}
            {reason === 'other' ? (
              <div className="field">
                <label htmlFor="door-words">{t('arrived.otherWords')}</label>
                <input id="door-words" className="input" value={words} onChange={(e) => setWords(e.target.value)} aria-required="true" data-testid="door-words" />
              </div>
            ) : null}
          </fieldset>

          {reasonReady ? (
            <section className="field" aria-labelledby="later-h" data-testid="come-back-later">
              <h3 id="later-h" className="eyebrow">
                {t('arrived.laterQuestion')}
              </h3>
              {later.isLoading ? (
                <p className="help">{t('app.loading')}</p>
              ) : later.data?.pickSpot && later.data.spots.length ? (
                <div className="chips" role="group" aria-labelledby="later-h">
                  {later.data.spots.map((s) => (
                    <Button key={s.position} size="sm" variant="secondary" disabled={busy} onClick={() => send('later', s.position)} data-testid={`later-${s.position}`}>
                      {t('arrived.moveTo', { time: formatClock(s.startMinutes) })}
                    </Button>
                  ))}
                </div>
              ) : (
                <p className="help" role="status" data-testid="later-none">
                  {later.data?.pickSpot ? t('arrived.noSpot') : t('arrived.laterOff')}
                </p>
              )}
              <div className="chips" style={{ marginTop: 10 }}>
                <Button disabled={busy} onClick={() => send('newDay')} data-testid="later-newday">
                  {t('arrived.newDay')}
                </Button>
              </div>
              <p className="help">{t('arrived.todayOnly')}</p>
            </section>
          ) : null}
        </>
      ) : null}

      {error ? (
        <div className="warn take" role="alert" data-testid="arrived-error">
          <Icon name="alert" />
          <div>{error}</div>
        </div>
      ) : null}
    </SheetContent>
  );
}
